import './Tabs.css';
import Welcome from '../Content/Welcome.js';

class Tabs {
  constructor(container, contentContainer, options = {}) {
    this.container = container;
    this.contentContainer = contentContainer;
    this.options = options;
    this.tabs = new Map();
    this.activeTabId = null;
    
    // Bind methods
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabClose = this.handleTabClose.bind(this);
  }
  
  /**
   * Render the tabs component
   */
  async render() {
    // Add container class
    this.container.className = 'tabs__container';
    this.contentContainer.className = 'tabs__content-container';
    
    // Create welcome tab container
    const welcomeContainer = document.createElement('div');
    
    // Create welcome tab component
    const welcome = new Welcome(welcomeContainer);
    await welcome.render();
    
    // Add the welcome tab
    this.addTab('welcome', 'Welcome', welcomeContainer, { closable: false });
    
    // Set up event listeners
    this.setupEventListeners();
    
    return this;
  }
  
  /**
   * Set up event listeners for the tabs
   */
  setupEventListeners() {
    this.container.addEventListener('click', (event) => {
      const tabElement = event.target.closest('.tabs__item');
      if (tabElement) {
        const closeButton = event.target.closest('.tabs__close');
        if (closeButton) {
          this.handleTabClose(tabElement.dataset.tabId);
        } else {
          this.handleTabClick(tabElement.dataset.tabId);
        }
      }
    });
  }
  
  /**
   * Add a new tab
   */
  addTab(id, title, content, options = {}) {
    const tabId = id || `tab-${Date.now()}`;
    const closable = options.closable !== undefined ? options.closable : true;
    
    // Create tab header
    const tab = document.createElement('div');
    tab.className = 'tabs__item';
    tab.dataset.tabId = tabId;
    
    // Special styling and content for welcome tab
    if (tabId === 'welcome') {
      tab.classList.add('tabs__item--welcome');
      tab.innerHTML = `
        <div class="tabs__logo">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#181818"/>
            <path d="M7 12H17M12 7V17" stroke="#39FF14" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <span class="tabs__title">${title}</span>
      `;
    } else {
      tab.innerHTML = `
        <span class="tabs__title">${title}</span>
        ${closable ? '<span class="tabs__close" title="Close Tab"><span class="material-symbols-outlined">close</span></span>' : ''}
      `;
    }
    
    // Create tab content
    const tabContent = document.createElement('div');
    tabContent.className = 'tabs__content';
    tabContent.dataset.tabId = tabId;
    
    if (typeof content === 'string') {
      tabContent.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      tabContent.appendChild(content);
    }
    
    this.tabs.set(tabId, { 
      id: tabId, 
      title, 
      content: tabContent,
      tab, 
      closable 
    });
    
    // Add to DOM
    this.container.appendChild(tab);
    this.contentContainer.appendChild(tabContent);
    
    // Activate the tab if it's the first one or if specified
    if (this.tabs.size === 1 || options.active) {
      this.activateTab(tabId);
    }
    
    return tabId;
  }
  
  /**
   * Activate a tab
   */
  activateTab(tabId) {
    if (!this.tabs.has(tabId)) return false;
    
    // Deactivate current active tab
    if (this.activeTabId) {
      const activeTab = this.tabs.get(this.activeTabId);
      if (activeTab) {
        activeTab.tab.classList.remove('active');
        activeTab.content.classList.remove('active');
      }
    }
    
    // Activate new tab
    const newActiveTab = this.tabs.get(tabId);
    newActiveTab.tab.classList.add('active');
    newActiveTab.content.classList.add('active');
    this.activeTabId = tabId;
    
    // Emit custom event
    const event = new CustomEvent('tab:activate', { 
      detail: { tabId },
      bubbles: true 
    });
    this.container.dispatchEvent(event);
    
    return true;
  }
  
  /**
   * Handle tab click
   */
  handleTabClick(tabId) {
    this.activateTab(tabId);
  }
  
  /**
   * Handle tab close
   */
  handleTabClose(tabId) {
    if (!this.tabs.has(tabId)) return false;
    
    const tab = this.tabs.get(tabId);
    if (!tab.closable) return false;
    
    // Remove from DOM
    tab.tab.remove();
    tab.content.remove();
    
    // Remove from tabs map
    this.tabs.delete(tabId);
    
    // If it was the active tab, activate another
    if (this.activeTabId === tabId) {
      if (this.tabs.size > 0) {
        this.activateTab(this.tabs.keys().next().value);
      } else {
        this.activeTabId = null;
      }
    }
    
    // Emit custom event
    const event = new CustomEvent('tab:close', { 
      detail: { tabId },
      bubbles: true 
    });
    this.container.dispatchEvent(event);
    
    return true;
  }
  
  /**
   * Get the active tab ID
   */
  getActiveTabId() {
    return this.activeTabId;
  }
  
  /**
   * Check if tab exists
   */
  hasTab(tabId) {
    return this.tabs.has(tabId);
  }
}

export default Tabs; 